<!--
  Flexi — General tab, as drawn in the #6191 design: the advisor the business
  sees on the left, the dialog with that advisor on the right.

  The advisor is the business's primary HSE advisor, resolved by the backend:
  the first active advisor in its Flexi › Settings list. With none active the
  card shows the empty state.

  The tab strip already names this tab, so the page heading stays for screen
  readers only. It is bound to FLEXI_OVERVIEW_TAB, the key the strip renders,
  so the two can never drift apart.
-->
<div class="fadv">
  <h2 class="fadv-sr-only">{{ 'FLEXI_OVERVIEW_TAB' | translate }}</h2>

  <div class="fadv__grid">

    <!-- ═══════════ Your HSE advisor ═══════════ -->
    <section class="fadv-card fadv-advisor" aria-labelledby="fadv-advisor-heading">
      <h3 class="fadv-advisor__heading" id="fadv-advisor-heading">
        {{ 'FLEXI_ADVISOR.YOUR_ADVISOR' | translate }}
      </h3>

      <ng-container *ngIf="userData != null; else noAdvisor">
        <div class="fadv-advisor__who">
          <!-- Photo when the advisor record has one, initials when it does not -->
          <img
            *ngIf="userData.Image"
            class="fadv-advisor__avatar"
            [src]="userData.Image"
            [alt]="userData?.Name || ('HSE_ADVISOR' | translate)"
          />
          <span
            *ngIf="!userData.Image"
            class="fadv-advisor__avatar fadv-advisor__avatar--initials"
            aria-hidden="true"
          >{{ initials(userData?.Name) }}</span>

          <div class="fadv-advisor__meta">
            <p class="fadv-advisor__name">{{ userData?.Name }}</p>
            <p class="fadv-advisor__role">
              {{ 'FLEXI_ADVISOR.SUBTITLE' | translate: { app: applicationName } }}
            </p>
          </div>
        </div>

        <ul class="fadv-advisor__contact">
          <li *ngIf="userData?.Email">
            <i class="pi pi-envelope" [attr.aria-label]="'EMAILID' | translate" role="img"></i>
            <a [href]="'mailto:' + userData.Email">{{ userData.Email }}</a>
          </li>
          <li *ngIf="userData?.Mobile">
            <i class="pi pi-phone" [attr.aria-label]="'MOBILE' | translate" role="img"></i>
            <a [href]="'tel:' + userData.Mobile">{{ userData.Mobile }}</a>
          </li>
          <li *ngIf="advisorLocation">
            <i class="pi pi-map-marker" [attr.aria-label]="'ADDRESS' | translate" role="img"></i>
            <span>{{ advisorLocation }}</span>
          </li>
        </ul>

        <button
          type="button"
          class="fadv-btn fadv-btn--primary fadv-advisor__message"
          *ngIf="!isPortalRemoteReadOnly"
          (click)="focusComposer()"
        >
          {{ 'FLEXI_ADVISOR.SEND_MESSAGE' | translate }}
        </button>
      </ng-container>

      <!-- No active advisor for this business -->
      <ng-template #noAdvisor>
        <div class="fadv-advisor__blank">
          <span class="fadv-advisor__blank-icon" aria-hidden="true">
            <i class="pi pi-user"></i>
          </span>
          <p class="fadv-advisor__blank-title">{{ 'NO_ADVISOR' | translate }}</p>
          <p class="fadv-advisor__blank-hint">{{ 'FLEXI_ADVISOR.NO_ADVISOR_HINT' | translate }}</p>
        </div>
      </ng-template>
    </section>

    <!-- ═══════════ Dialog with the advisor ═══════════ -->
    <section class="fadv-card fadv-chat" aria-labelledby="fadv-chat-heading">
      <header class="fadv-chat__head">
        <h3 class="fadv-chat__title" id="fadv-chat-heading">{{ 'DIALOG_WITH_OHS' | translate }}</h3>

        <span class="fadv-pill fadv-pill--unread" *ngIf="unreadCount > 0">
          {{ 'FLEXI_ADVISOR.UNREAD_COUNT' | translate: { count: unreadCount } }}
        </span>

        <span class="fadv-pill fadv-pill--muted" *ngIf="isPortalRemoteReadOnly">
          <i class="pi pi-lock" aria-hidden="true"></i>
          {{ 'FLEXI_ADVISOR.READ_ONLY' | translate }}
        </span>
      </header>

      <div class="fadv-chat__box">
        <!-- Loading -->
        <div class="fadv-chat__state" *ngIf="isLoadingMessages">
          <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
          <span>{{ 'FLEXI_COMPLIANCE.LOADING' | translate }}</span>
        </div>

        <!-- Empty -->
        <p class="fadv-chat__state"
           *ngIf="!isLoadingMessages && (!messages || messages.length === 0)">
          {{ (isPortalRemoteReadOnly ? 'NO_RECORDS_FOUND' : 'FLEXI_ADVISOR.EMPTY_THREAD_HINT') | translate }}
        </p>

        <!-- Thread -->
        <div class="fadv-thread" *ngIf="!isLoadingMessages && messages?.length > 0">
          <ng-container *ngFor="let day of messageDays; trackBy: trackByDay">

            <p class="fadv-thread__day" *ngIf="day.date">
              <span *ngIf="day.relative === 0">{{ 'TODAY' | translate }}</span>
              <span *ngIf="day.relative === 1">{{ 'YESTERDAY' | translate }}</span>
              <span *ngIf="day.relative === -1">{{ day.date | date: 'dd.MM.yyyy' }}</span>
            </p>

            <article
              *ngFor="let m of day.items"
              class="fadv-msg"
              [class.fadv-msg--own]="isOwnMessage(m)"
              [class.fadv-msg--unread]="isUnread(m)"
            >
              <span class="fadv-msg__avatar" aria-hidden="true">
                {{ initials(isOwnMessage(m) ? currentUsername : getMessageDisplayName(m)) }}
              </span>

              <div class="fadv-msg__main">
                <div class="fadv-msg__meta">
                  <span class="fadv-msg__from">
                    {{ isOwnMessage(m) ? ('YOU' | translate) : getMessageDisplayName(m) }}
                  </span>
                  <span class="fadv-msg__time">{{ m.createdAt | date: 'HH:mm' }}</span>

                  <span class="fadv-msg__unread" *ngIf="isUnread(m)">
                    <span class="fadv-msg__dot" aria-hidden="true"></span>
                    {{ 'FLEXI_ADVISOR.UNREAD' | translate }}
                  </span>

                  <button
                    *ngIf="isAdmin && !isPortalRemoteReadOnly"
                    type="button"
                    class="fadv-msg__delete"
                    [title]="'DELETE' | translate"
                    [attr.aria-label]="'DELETE' | translate"
                    (click)="deleteMessage(m)"
                  >
                    <i class="pi pi-trash" aria-hidden="true"></i>
                  </button>
                </div>

                <button
                  type="button"
                  class="fadv-msg__bubble"
                  (click)="openMessage(m)"
                  [title]="'VIEW' | translate"
                >
                  {{ m.messageContent || m.text || '' }}
                </button>
              </div>
            </article>
          </ng-container>
        </div>
      </div>

      <!-- ─── Compose ─── -->
      <div class="fadv-compose" *ngIf="!isPortalRemoteReadOnly">

        <!-- Recipients — advisors (admins) only; a customer always writes to the advisor -->
        <div class="fadv-field" *ngIf="showRecipientField">
          <label class="fadv-field__label" for="flexiRecipients">{{ 'TO' | translate }}</label>
          <p-multiSelect
            inputId="flexiRecipients"
            [options]="recipientOptions"
            [(ngModel)]="selectedRecipients"
            optionLabel="name"
            [filter]="true"
            [showClear]="true"
            [placeholder]="'CHOOSE' | translate"
            [disabled]="isLoadingRecipients || isLoadingMessages"
            appendTo="body"
            styleClass="fadv-multiselect"
          ></p-multiSelect>
        </div>

        <div class="fadv-field">
          <label class="fadv-field__label" for="flexiMessage">
            <ng-container *ngIf="isAdmin">{{ 'CONTENT' | translate }}</ng-container>
            <ng-container *ngIf="!isAdmin">
              {{ 'FLEXI_ADVISOR.TO' | translate: { name: userData?.Name || ('HSE_ADVISOR' | translate) } }}
            </ng-container>
          </label>
          <textarea
            #messageInput
            id="flexiMessage"
            pTextarea
            rows="3"
            [(ngModel)]="newMessageText"
            (keydown.control.enter)="sendMessage()"
            [placeholder]="'TYPE_MESSAGE' | translate"
            [autoResize]="true"
            class="fadv-compose__input"
            [disabled]="isLoadingMessages"
          ></textarea>
        </div>

        <div class="fadv-compose__foot">
          <button
            type="button"
            class="fadv-btn fadv-btn--primary"
            (click)="sendMessage()"
            [disabled]="
              isSubmitting ||
              !newMessageText?.trim() ||
              (isAdmin && !selectedRecipients?.length) ||
              isLoadingMessages
            "
          >
            <i class="pi pi-spin pi-spinner" *ngIf="isSubmitting" aria-hidden="true"></i>
            <span translate>SEND</span>
          </button>
        </div>
      </div>

      <!-- Portal view: the thread is readable, nothing can be sent from it -->
      <p class="fadv-chat__readonly" *ngIf="isPortalRemoteReadOnly">
        <i class="pi pi-lock" aria-hidden="true"></i>
        {{ 'FLEXI_ADVISOR.READ_ONLY_HINT' | translate }}
      </p>
    </section>
  </div>
</div>

<!-- ═══════════════════════════════════════
     Message detail dialog
     ═══════════════════════════════════════ -->
<p-dialog
  [(visible)]="showMessageDialog"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [resizable]="false"
  [baseZIndex]="10000"
  [style]="{ width: '560px', 'max-width': '95vw' }"
  (onHide)="closeMessageDialog()"
  appendTo="body"
  styleClass="fadv-dialog"
>
  <!-- Keep the header template present for the PrimeNG content query.
       Guard null inside instead. -->
  <ng-template pTemplate="header">
    <div class="fadv-dialog__head" *ngIf="selectedMessage">
      <span class="fadv-dialog__avatar" aria-hidden="true">
        {{ initials(selectedMessage?.fromUserName || selectedMessage?.username) }}
      </span>
      <div class="fadv-dialog__meta">
        <p class="fadv-dialog__from">
          {{
            selectedMessage?.fromUserName ||
              selectedMessage?.username ||
              ('HSE_ADVISOR' | translate)
          }}
        </p>
        <p class="fadv-dialog__sub">
          {{ 'TO' | translate }}: {{ selectedMessage?.toUserName || ('HSE_ADVISOR' | translate) }}
          · {{ (selectedMessage?.createdAt | date: 'dd.MM.yyyy HH:mm') || '' }}
        </p>
      </div>
    </div>
  </ng-template>

  <div class="fadv-dialog__body" *ngIf="selectedMessage">
    <p class="fadv-dialog__text">{{ selectedMessage.text }}</p>
  </div>

  <ng-template pTemplate="footer">
    <div class="fadv-dialog__foot">
      <button type="button" class="fadv-btn fadv-btn--neutral" (click)="closeMessageDialog()">
        <span translate>CLOSE</span>
      </button>
      <button
        type="button"
        class="fadv-btn fadv-btn--primary"
        *ngIf="!isPortalRemoteReadOnly"
        (click)="replyToMessage()"
      >
        <i class="pi pi-reply" aria-hidden="true"></i>
        <span translate>REPLY</span>
      </button>
    </div>
  </ng-template>
</p-dialog>
